import { createContext, useContext, useEffect, useId } from "react";

/** @const {!React.Context<{report: function(string, ?{section: ?string, label: string})}>} */
export const UnsavedChangesContext = createContext({ report: () => {} });

/** @const {!React.Context<?string>} Section key of the enclosing settings section. */
export const SettingsSectionContext = createContext(null);

/**
 * Registers a card's unsaved state with the enclosing settings page so the
 * side nav can mark the section and leaving the page can warn.
 *
 * @param {string} label Human name used in the leave-page prompt, e.g. "Lock Dates".
 * @param {boolean} dirty
 */
export const useReportUnsaved = (label, dirty) => {
  const { report } = useContext(UnsavedChangesContext);
  const section = useContext(SettingsSectionContext);
  const id = useId();

  useEffect(() => {
    report(id, dirty ? { section, label } : null);
  }, [report, id, dirty, section, label]);

  useEffect(() => () => report(id, null), [report, id]);
};

/**
 * @param {!Array<string>} labels
 * @return {string} "A", "A and B", or "A, B and C".
 */
export const joinLabels = labels => {
  const unique = [...new Set(labels.filter(Boolean))];
  if (unique.length <= 1) return unique[0] || "";
  return `${unique.slice(0, -1).join(", ")} and ${unique[unique.length - 1]}`;
};

/**
 * True when a click on this anchor would navigate away inside the app.
 *
 * @param {!MouseEvent} event
 * @param {?HTMLAnchorElement} anchor
 * @param {string} currentPath Path of the settings page (navigation within it is allowed).
 * @return {boolean}
 */
export const isLeavingClick = (event, anchor, currentPath) => {
  if (!anchor || event.defaultPrevented || event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  if (anchor.target && anchor.target !== "_self") return false;
  if (anchor.hasAttribute("download")) return false;
  const href = anchor.getAttribute("href");
  if (!href || href.startsWith("#")) return false;
  const url = new URL(anchor.href, window.location.href);
  if (url.origin !== window.location.origin) return false;
  return !url.pathname.startsWith(currentPath);
};
